import { ArrowRight } from "lucide-react";

/**
 * Inline contextual CTA rendered at the bottom of a section's content
 * container so the conversion path lives *inside* the section, not as a
 * separate block between sections.
 *
 * Two variants:
 *  - "strip" : green pill button + supporting copy below it (high-intent)
 *  - "soft"  : single underlined green link (low-intent / proof beats)
 */
type SectionCtaProps = {
  variant?: "strip" | "soft";
  copy: string;
  label: string;
  href?: string;
};

export function SectionCta({
  variant = "strip",
  copy,
  label,
  href = "/book",
}: SectionCtaProps) {
  if (variant === "soft") {
    return (
      <div className="relative z-10 flex justify-center text-center mt-10 md:mt-12">
        <a
          href={href}
          className="group inline-flex items-center gap-1.5 font-sans font-semibold underline underline-offset-4 decoration-[rgba(72,244,90,0.4)] hover:decoration-[#48F45A] transition-colors"
          style={{ fontSize: "14.5px", color: "#3BCC48" }}
        >
          {copy}
          <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
        </a>
      </div>
    );
  }

  // strip variant: centered green button with context text below it
  return (
    <div className="relative z-10 flex flex-col items-center justify-center text-center mt-12 md:mt-16">
      <a
        href={href}
        className="group inline-flex items-center justify-center gap-2 rounded-full font-sans font-bold text-white transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48F45A]"
        style={{
          background: "linear-gradient(135deg, #2BC845, #48F45A)",
          height: 52,
          padding: "0 32px",
          fontSize: "15px",
          boxShadow: "0 10px 24px rgba(72,244,90,0.30)",
        }}
      >
        <span>{label}</span>
        <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
      </a>
      <p
        className="font-sans text-center mt-3 max-w-[620px]"
        style={{
          fontSize: "14px",
          lineHeight: 1.45,
          color: "#536B89",
          fontWeight: 500,
        }}
      >
        {copy}
      </p>
    </div>
  );
}
